import type {
  AdminOrderListParams,
  AdminOrdersResponse,
  ApiError,
  CreateCheckoutResponse,
  HealthResponse,
  OrderReceipt,
  OwnedParcel,
  Paginated,
  Parcel,
  ParcelListParams,
  ParcelUpdate,
  ParcelUpdateListParams,
  RetryGrantResponse,
  SessionUser,
} from "@/shared/types/api";
import {
  clearAccessToken,
  getAccessToken,
} from "@/shared/utils/localStorageHelpers";
import axios, { AxiosError, type AxiosInstance } from "axios";
import { notifyAuthFailure } from "./authBridge";

/**
 * The only HTTP client in the app. It talks to `server/` (SPEC §6) and nothing
 * else — the browser never reaches the Filedgr API or Stripe secrets directly
 * (SPEC §3). Default base URL is `/api`, same-origin, which is also what the
 * Docker image serves.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
  headers: { "Content-Type": "application/json" },
});

/** `exp` is the only claim we read client-side; the server verifies the rest. */
const isTokenExpired = (token: string): boolean => {
  try {
    const [, payload] = token.split(".");
    if (!payload) return true;
    const claims = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    ) as { exp?: number };
    if (!claims.exp) return false;
    // 15s skew so a request in flight at the boundary is not rejected twice.
    return Date.now() >= claims.exp * 1000 - 15_000;
  } catch {
    // Unparseable token is a broken session — treat it as expired.
    return true;
  }
};

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (!token) return config;

  if (isTokenExpired(token)) {
    clearAccessToken();
    notifyAuthFailure("token-expired");
    return Promise.reject(
      new axios.Cancel("Session expired") as unknown as Error
    );
  }

  config.headers.set("Authorization", `Bearer ${token}`);
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const status = error.response?.status;
    if (status === 401) notifyAuthFailure("unauthorized");
    if (status === 403) notifyAuthFailure("forbidden");
    return Promise.reject(error);
  }
);

/**
 * Narrow an unknown thrown value to the server's error envelope so screens can
 * branch on `code` (`PARCEL_UNAVAILABLE`, …) and map it to an `errors.*` key.
 */
export const toApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const body = (error as AxiosError<ApiError>).response?.data;
    if (body && typeof body.message === "string") return body;
    return {
      error: error.code || "network_error",
      message: error.message,
    };
  }
  if (error instanceof Error) {
    return { error: "unexpected", message: error.message };
  }
  return { error: "unexpected", message: String(error) };
};

// ---------------------------------------------------------------------------
// Public catalogue
// ---------------------------------------------------------------------------

/** `GET /api/parcels` — public, served from the server's cache. */
export const getParcels = async (
  params: ParcelListParams = {}
): Promise<Paginated<Parcel>> => {
  const { data } = await apiClient.get<Paginated<Parcel>>("/parcels", {
    params: {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 12,
      ...(params.status ? { status: params.status } : {}),
    },
  });
  return data;
};

/** `GET /api/parcels/:vaultId` — public. */
export const getParcel = async (vaultId: string): Promise<Parcel> => {
  const { data } = await apiClient.get<Parcel>(
    `/parcels/${encodeURIComponent(vaultId)}`
  );
  return data;
};

/** `GET /api/parcels/:vaultId/updates` — public update feed. */
export const getParcelUpdates = async (
  vaultId: string,
  params: ParcelUpdateListParams = {}
): Promise<Paginated<ParcelUpdate>> => {
  const { data } = await apiClient.get<Paginated<ParcelUpdate>>(
    `/parcels/${encodeURIComponent(vaultId)}/updates`,
    {
      params: { page: params.page ?? 1, pageSize: params.pageSize ?? 10 },
    }
  );
  return data;
};

// ---------------------------------------------------------------------------
// Checkout (auth required)
// ---------------------------------------------------------------------------

/**
 * `POST /api/checkout` — reserves the parcel and opens a Stripe Checkout
 * Session. Login is mandatory *before* this call (SPEC C2: the viewer grant
 * 404s unless the buyer already exists with a WALLET credential).
 * Redirect with `window.location.assign(res.url)`.
 */
export const createCheckoutSession = async (
  vaultId: string
): Promise<CreateCheckoutResponse> => {
  const { data } = await apiClient.post<CreateCheckoutResponse>("/checkout", {
    vaultId,
  });
  return data;
};

/**
 * `GET /api/orders/:sessionId` — poll after the Stripe redirect. Payment and
 * the VIEWER grant settle independently; `status: 'paid'` with
 * `grantStatus: 'failed'` is a valid state that must not read as a failed
 * purchase (SPEC §7).
 */
export const getOrder = async (sessionId: string): Promise<OrderReceipt> => {
  const { data } = await apiClient.get<OrderReceipt>(
    `/orders/${encodeURIComponent(sessionId)}`
  );
  return data;
};

// ---------------------------------------------------------------------------
// Buyer (auth required)
// ---------------------------------------------------------------------------

/**
 * `GET /api/me` — the server's own view of the session. It is the only
 * authority on `isAdmin`: `ADMIN_EMAILS` is a server secret, so the client
 * never infers the role itself. Doubles as a cheap "is my token still good?".
 */
export const getMe = async (): Promise<SessionUser> => {
  const { data } = await apiClient.get<SessionUser>("/me");
  return data;
};

/**
 * `POST /api/me/filedgr-user` — makes sure this account exists on Filedgr.
 *
 * Called once straight after login. The server forwards our idToken to Filedgr's
 * `GET /users`, which CREATES the account if the platform has never seen it. That
 * matters because the viewer grant identifies the buyer by email and cannot grant to
 * an unknown user: without this, a first-time buyer can pay and then have the grant
 * fail terminally. `filedgr-web-app` gets this for free — its global saga calls
 * `getUserData()` after login for the same reason.
 *
 * Best-effort by design. Resolves rather than throwing, and the response says what
 * happened, so a platform blip never surfaces as a broken login.
 */
export const ensureFiledgrUser = async (): Promise<{
  provisioned: boolean;
  hasWallet: boolean;
}> => {
  const { data } = await apiClient.post<{ provisioned: boolean; hasWallet: boolean }>(
    "/me/filedgr-user"
  );
  return data;
};

/** `GET /api/me/parcels` — the parcels this account bought. */
export const getMyParcels = async (
  params: { page?: number; pageSize?: number } = {}
): Promise<Paginated<OwnedParcel>> => {
  const { data } = await apiClient.get<Paginated<OwnedParcel>>("/me/parcels", {
    params: { page: params.page ?? 1, pageSize: params.pageSize ?? 20 },
  });
  return data;
};

// ---------------------------------------------------------------------------
// Admin (auth + ADMIN_EMAILS)
// ---------------------------------------------------------------------------

/**
 * `GET /api/admin/orders` — read-only ops view. Carries the queue counters and
 * the deployment's `grantStrategy` alongside the page, so the screen can say
 * what a pending grant currently *means* (SPEC §7.1).
 */
export const getAdminOrders = async (
  params: AdminOrderListParams = {}
): Promise<AdminOrdersResponse> => {
  const { data } = await apiClient.get<AdminOrdersResponse>("/admin/orders", {
    params: {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 25,
      ...(params.status ? { status: params.status } : {}),
      ...(params.grantStatus ? { grantStatus: params.grantStatus } : {}),
      ...(params.grantStrategy ? { grantStrategy: params.grantStrategy } : {}),
      ...(params.buyerEmail ? { buyerEmail: params.buyerEmail } : {}),
      ...(params.awaitingManualGrant ? { awaitingManualGrant: true } : {}),
    },
  });
  return data;
};

/**
 * `POST /api/admin/orders/:id/retry-grant` — re-attempt an automatic grant.
 * Rejected with 400 under `GRANT_STRATEGY=manual`: there is nothing to re-run,
 * the fix is to grant in the Filedgr dashboard and call `markGranted`.
 */
export const retryGrant = async (
  orderId: string
): Promise<RetryGrantResponse> => {
  const { data } = await apiClient.post<RetryGrantResponse>(
    `/admin/orders/${encodeURIComponent(orderId)}/retry-grant`
  );
  return data;
};

/**
 * `POST /api/admin/orders/:id/mark-granted` — records a VIEWER permission an
 * admin applied by hand in the Filedgr dashboard, with who and when
 * (docs/PROVISIONING.md §8b step 4). The closing half of the manual strategy,
 * and what stops two people fulfilling the same order twice.
 */
export const markGranted = async (
  orderId: string
): Promise<RetryGrantResponse> => {
  const { data } = await apiClient.post<RetryGrantResponse>(
    `/admin/orders/${encodeURIComponent(orderId)}/mark-granted`
  );
  return data;
};

// ---------------------------------------------------------------------------
// Ops
// ---------------------------------------------------------------------------

/** `GET /api/health` — liveness. */
export const getHealth = async (): Promise<HealthResponse> => {
  const { data } = await apiClient.get<HealthResponse>("/health");
  return data;
};
